// @ts-strict-ignore
import balanced from 'balanced-match';

import { getComposedRegex } from '@/utils/composedRegex';
import { promisify } from '@/utils/promisify';

const balancedAsync = promisify(balanced);

const paranthesisRegex = new RegExp(/[()]/);
const booleanOperatorRegex = new RegExp(/ [-|+] /);
const booleanAndRegex = new RegExp(/ AND /);
const booleanOrRegex = new RegExp(/ OR /);
const booleanNotRegex = new RegExp(/ NOT /);

const sanitizeRegex = getComposedRegex(
  'g',
  paranthesisRegex,
  booleanAndRegex,
  booleanOrRegex,
  booleanNotRegex
);

export const booleanRegex = getComposedRegex(
  'g',
  booleanOperatorRegex,
  booleanAndRegex,
  booleanOrRegex,
  booleanNotRegex
);

const QUOTES = {
  '"""': '"""',
  '“”“': '”“”',
  '“““': '”””',
  '""': '""',
  '“”': '“”',
  '““': '””',
  '"': '"',
  '“': '”',
};

export const hasParanthesis = (input: string) => paranthesisRegex.test(input);

export const checkParanthesisBalanced = async (input: string) => {
  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const balancedMatch = await balancedAsync('(', ')', input);
      if (!balancedMatch) break;

      if (hasParanthesis(balancedMatch.body)) {
        return await checkParanthesisBalanced(balancedMatch.body);
      }

      input = balancedMatch.pre + balancedMatch.post;
    }

    return !hasParanthesis(input);
  } catch (error) {
    console.error('Error checking paranthesis balance:', error);
    return false;
  }
};

const isQuoteBalanced = async (
  preQuote: string,
  postQuote: string,
  keyword: string
) => {
  const balancedMatch = await balancedAsync(preQuote, postQuote, keyword);

  if (!balancedMatch) return false;

  if (balancedMatch.post) {
    const postBalanced = await checkQuotesBalanced(balancedMatch.post);
    if (!postBalanced) return false;
  }

  return true;
};

export const checkQuotesBalanced = async (keyword: string) => {
  try {
    for (const [preQuote, postQuote] of Object.entries(QUOTES)) {
      if (keyword.includes(preQuote)) {
        return await isQuoteBalanced(preQuote, postQuote, keyword);
      }
    }
    return true;
  } catch {
    return false;
  }
};

const sanitizeInput = (input: string) => {
  // handle boolean text operators
  const formattedString = input.replace(sanitizeRegex, (match) => {
    switch (match) {
      case booleanAndRegex.source:
        return ' + ';
      case booleanOrRegex.source:
        return ' | ';
      case booleanNotRegex.source:
        return ' - ';
      case paranthesisRegex.source:
      default:
        return '';
    }
  });

  // handle boolean operators hanging at ends
  return formattedString.replace(/^[+\-|]|[+\-|]$/g, ' $& ');
};

export const getKeywords = (input: string) => {
  const inputString = sanitizeInput(input);
  const keywords = inputString.split(booleanOperatorRegex);
  return keywords.map((keyword) => keyword.trim());
};
